import React, { useState } from 'react';
import { CheckSquare as CheckSquareIcon, Check, Paperclip, FileText, Eye } from 'lucide-react';
import FileViewer from './FileViewer';

const ChecklistViewer = ({ content, onComplete, isCompleted, previousResponse, isAdmin = false, children }) => {
    const [checklist, setChecklist] = useState(() => {
        try {
            if (typeof content === 'object' && content !== null) {
                return content;
            }
            return JSON.parse(content);
        } catch (e) {
            console.error("Error parsing checklist content:", e);
            return { items: [] };
        }
    });

    const [completed, setCompleted] = useState(() => {
        if (previousResponse) {
            try {
                const parsed = typeof previousResponse === 'string' ? JSON.parse(previousResponse) : previousResponse;
                return new Set(parsed.completedItems || []);
            } catch (e) {
                return new Set();
            }
        }
        return new Set();
    });

    // Sync state with previousResponse prop changes
    React.useEffect(() => {
        if (previousResponse) {
            try {
                const parsed = typeof previousResponse === 'string' ? JSON.parse(previousResponse) : previousResponse;
                setCompleted(new Set(parsed.completedItems || []));
            } catch (e) {
                setCompleted(new Set());
            }
        } else {
            // Effectively clear when previousResponse is null (Retake approved)
            setCompleted(new Set());
        }
    }, [previousResponse]);

    const [previewFile, setPreviewFile] = useState(null);

    const handleToggle = (itemId) => {
        if (!isCompleted && !isAdmin) {
            setCompleted(prev => {
                const newSet = new Set(prev);
                if (newSet.has(itemId)) {
                    newSet.delete(itemId);
                } else {
                    newSet.add(itemId);
                }
                return newSet;
            });
        }
    };

    const handleCompleteChecklist = () => {
        const requiredItems = checklist.items.filter(item => item.required !== false);
        const allRequiredComplete = requiredItems.every(item => completed.has(item.id));

        if (!allRequiredComplete) {
            alert('Please complete all required items before submitting.');
            return;
        }

        const responseData = {
            completedItems: Array.from(completed),
            totalItems: checklist.items.length,
            requiredCompleted: requiredItems.length,
            completedAt: new Date().toISOString()
        };

        onComplete(JSON.stringify(responseData));
    };

    if (!checklist.items || checklist.items.length === 0) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <CheckSquareIcon className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                <p className="text-yellow-800">No checklist items available</p>
            </div>
        );
    }

    // Group items by category
    const categories = [...new Set(checklist.items.map(item => item.category || 'General'))];
    const groupedItems = categories.map(category => ({
        category,
        items: checklist.items.filter(item => (item.category || 'General') === category)
    }));

    const totalItems = checklist.items.length;
    const completedCount = completed.size;
    const requiredItems = checklist.items.filter(item => item.required !== false);
    const allRequiredComplete = requiredItems.every(item => completed.has(item.id));

    return (
        <div className={isAdmin ? "space-y-4" : "space-y-6"}>
            {/* Checklist Header - Compact for Admin */}
            <div className={`bg-green-50 border border-green-200 rounded-lg ${isAdmin ? 'p-3' : 'p-4'}`}>
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <CheckSquareIcon className={`text-green-600 ${isAdmin ? 'w-4 h-4' : 'w-5 h-5'}`} />
                            <h3 className={`font-semibold text-green-900 ${isAdmin ? 'text-sm' : ''}`}>Checklist</h3>
                        </div>
                        <div className="text-sm text-green-700">
                            <span>{requiredItems.length} required</span>
                            <span className="mx-2">•</span>
                            <span>{totalItems - requiredItems.length} optional</span>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-gray-600">Progress</p>
                        <p className={`font-bold text-green-700 ${isAdmin ? 'text-lg' : 'text-2xl'}`}>
                            {completedCount}/{totalItems}
                        </p>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-3">
                    <div className="w-full bg-green-200 rounded-full h-2">
                        <div
                            className="bg-green-600 h-2 rounded-full transition-all duration-300"
                            style={{
                                width: `${(completedCount / totalItems) * 100}%`
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Checklist Items by Category */}
            <div className={isAdmin ? "space-y-4" : "space-y-6"}>
                {groupedItems.map((group, gIndex) => (
                    <div key={gIndex} className={`bg-white border border-gray-200 rounded-lg ${isAdmin ? 'p-3' : 'p-4'}`}>
                        <h4 className={`font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200 ${isAdmin ? 'text-sm' : ''}`}>
                            {group.category}
                        </h4>

                        <div className={isAdmin ? "space-y-2" : "space-y-3"}>
                            {group.items.map((item, index) => {
                                const isItemCompleted = completed.has(item.id);
                                const isRequired = item.required !== false;

                                return (
                                    <div
                                        key={item.id || index}
                                        className={`${isAdmin ? 'border p-2' : 'border-2 p-4'} rounded-lg transition-all ${isItemCompleted
                                            ? 'border-green-300 bg-green-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <label className={`flex items-start gap-3 ${!isCompleted && !isAdmin ? 'cursor-pointer' : 'cursor-default'}`}>
                                            <input
                                                type="checkbox"
                                                checked={isItemCompleted}
                                                onChange={() => handleToggle(item.id)}
                                                disabled={isCompleted || isAdmin}
                                                className={`mt-0.5 text-green-600 rounded focus:ring-2 focus:ring-green-500 disabled:opacity-50 ${isAdmin ? 'w-4 h-4' : 'w-5 h-5'}`}
                                            />

                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <p className={`${isAdmin ? 'text-sm' : 'font-medium'} ${isItemCompleted ? 'text-green-900 line-through' : 'text-gray-900'}`}>
                                                        {item.text}
                                                    </p>
                                                    {isRequired && (
                                                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                                                            Required
                                                        </span>
                                                    )}
                                                </div>

                                                {item.description && (
                                                    <p className={`text-gray-600 ${isAdmin ? 'text-xs' : 'text-sm'}`}>
                                                        {item.description}
                                                    </p>
                                                )}

                                                {/* Item Attachments */}
                                                {item.attachments && item.attachments.length > 0 && (
                                                    <div className={`mt-2 pt-2 border-t border-gray-200`}>
                                                        <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                                                            <Paperclip className="w-3 h-3" />
                                                            Attachments
                                                        </p>
                                                        <div className="space-y-1">
                                                            {item.attachments.map((file, aIndex) => (
                                                                <button
                                                                    key={aIndex}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        setPreviewFile(file);
                                                                    }}
                                                                    className="w-full text-left flex items-center gap-2 p-2 bg-primary/5 hover:bg-primary/10 rounded group transition-colors"
                                                                >
                                                                    <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                                                                    <span className="truncate text-sm text-gray-800">{file.file_name}</span>
                                                                    <Eye className="w-3 h-3 text-gray-400 group-hover:text-primary transition-colors ml-auto flex-shrink-0" />
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Inject Children (Proof/Signature) before Complete Button */}
            {!isCompleted && !isAdmin && children}

            {/* Complete Checklist Button - Hide for Admin */}
            {!isCompleted && !isAdmin && (
                <div className={`border-2 rounded-lg p-6 text-center ${allRequiredComplete ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-300'
                    }`}>
                    {allRequiredComplete ? (
                        <>
                            <CheckSquareIcon className="w-12 h-12 text-green-600 mx-auto mb-3" />
                            <h3 className="text-lg font-semibold text-green-900 mb-2">All Required Items Complete!</h3>
                            <p className="text-green-700 mb-4">You've completed all required checklist items.</p>
                            <button
                                onClick={handleCompleteChecklist}
                                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                            >
                                Submit Checklist
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="text-gray-700 mb-2">
                                Complete all required items to submit the checklist
                            </p>
                            <p className="text-sm text-gray-600">
                                {requiredItems.filter(item => completed.has(item.id)).length}/{requiredItems.length} required items completed
                            </p>
                        </>
                    )}
                </div>
            )}

            {/* File Viewer Modal */}
            <FileViewer
                file={previewFile}
                isOpen={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />
        </div>
    );
};

export default ChecklistViewer;

